import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type ProjectPhase = "backlog" | "in_progress" | "review" | "done";

export interface ClientProject {
  id: string;
  title: string;
  description: string | null;
  client_name: string | null;
  client_email: string | null;
  lead_id: string | null;
  phase: ProjectPhase;
  priority: number;
  due_date: string | null;
  budget: number | null;
  display_order: number;
  assigned_to: string | null;
  tags: string[];
  uses_jira_mode: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateClientProjectData {
  title: string;
  description?: string;
  client_name?: string;
  client_email?: string;
  lead_id?: string;
  phase?: ProjectPhase;
  priority?: number;
  due_date?: string;
  budget?: number;
  tags?: string[];
}

export interface UpdateClientProjectData {
  title?: string;
  description?: string;
  client_name?: string;
  client_email?: string;
  lead_id?: string;
  phase?: ProjectPhase;
  priority?: number;
  due_date?: string;
  budget?: number;
  display_order?: number;
  tags?: string[];
}

// Hook para listar projetos de clientes
export function useClientProjects() {
  return useQuery({
    queryKey: ["client-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_projects")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as ClientProject[];
    },
  });
}

// Hook para projetos agrupados por fase (para Kanban)
export function useClientProjectsByPhase() {
  const { data: projects, ...rest } = useClientProjects();

  const grouped = {
    backlog: projects?.filter((p) => p.phase === "backlog") || [],
    in_progress: projects?.filter((p) => p.phase === "in_progress") || [],
    review: projects?.filter((p) => p.phase === "review") || [],
    done: projects?.filter((p) => p.phase === "done") || [],
  };

  return { grouped, projects, ...rest };
}

// Hook para criar projeto
export function useCreateClientProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateClientProjectData) => {
      // Obter maior display_order da fase
      const phase = data.phase || "backlog";
      const { data: existing } = await supabase
        .from("client_projects")
        .select("display_order")
        .eq("phase", phase)
        .order("display_order", { ascending: false })
        .limit(1);

      const maxOrder = existing?.[0]?.display_order ?? -1;

      const { data: project, error } = await supabase
        .from("client_projects")
        .insert({
          title: data.title,
          description: data.description || null,
          client_name: data.client_name || null,
          client_email: data.client_email || null,
          lead_id: data.lead_id || null,
          phase: phase,
          priority: data.priority ?? 0,
          due_date: data.due_date || null,
          budget: data.budget || null,
          display_order: maxOrder + 1,
          tags: data.tags || [],
        })
        .select()
        .single();

      if (error) throw error;
      return project as ClientProject;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-projects"] });
      toast.success("Projeto criado com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao criar projeto: " + error.message);
    },
  });
}

// Hook para atualizar projeto
export function useUpdateClientProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateClientProjectData }) => {
      const { data: project, error } = await supabase
        .from("client_projects")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return project as ClientProject;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-projects"] });
    },
    onError: (error) => {
      toast.error("Erro ao atualizar projeto: " + error.message);
    },
  });
}

// Hook para mover projeto (drag and drop)
export function useMoveClientProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      newPhase,
      newOrder,
    }: {
      projectId: string;
      newPhase: ProjectPhase;
      newOrder: number;
    }) => {
      const { error } = await supabase
        .from("client_projects")
        .update({ phase: newPhase, display_order: newOrder })
        .eq("id", projectId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-projects"] });
    },
  });
}

// Hook para deletar projeto
export function useDeleteClientProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Propostas vinculadas ficam: budgets.client_project_id é ON DELETE SET
      // NULL (migration 20260925120000). Apagar aqui levava junto, em cascata,
      // assinaturas, parcelas e assinaturas recorrentes do cliente.
      const { error } = await supabase.from("client_projects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-projects"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-projects"] });
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      toast.success("Projeto removido!");
    },
    onError: () => {
      toast.error("Erro ao remover projeto");
    },
  });
}
